import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Merging Themes"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <h1 {...{
      "id": "merging-themes"
    }}>{`Merging Themes`}</h1>
    <p>{`Sometimes it's useful to split a theme across multiple files, use a preset as the basis for a custom theme, or combine two or more themes together.
Since themes are plain JavaScript objects, any merging strategy will work.
This guide shows a few common ways to merge themes together.`}</p>
    <h2 {...{
      "id": "using-a-preset"
    }}>{`Using a preset`}</h2>
    <p>{`To use a preset as the basis for a custom theme, it's recommended that you use a deep merge utility.
The `}<inlineCode parentName="p">{`theme-ui`}</inlineCode>{` package exports a preconfigured version of the `}<inlineCode parentName="p">{`deepmerge`}</inlineCode>{` package that can be used for this.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`// example theme based on preset-future
import future from '@theme-ui/preset-future'
import { merge } from 'theme-ui'

export default merge(future, {
  fonts: {
    body: 'Montserrat, sans-serif',
  },
})
`}</code></pre>
    <h2 {...{
      "id": "multiple-files"
    }}>{`Multiple files`}</h2>
    <p>{`While there is absolutely nothing wrong with keeping an entire theme in a single file, you can split a theme into multiple files (or modules).`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`// example theme/colors.js
export default {
  text: '#000',
  background: '#fff',
  primary: '#07c',
}
`}</code></pre>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`// example theme/fonts.js
export default {
  body: 'system-ui, sans-serif',
  heading: 'Baskerville, Georgia, serif',
  monospace: 'Menlo, monospace',
}
`}</code></pre>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`// example theme/index.js
import colors from './colors'
import fonts from './fonts'

export default {
  colors,
  fonts,
}
`}</code></pre>
    <h2 {...{
      "id": "merging-theme-values-from-multiple-files"
    }}>{`Merging theme values from multiple files`}</h2>
    <p>{`For the core scales (`}<inlineCode parentName="p">{`colors`}</inlineCode>{`, `}<inlineCode parentName="p">{`fontSizes`}</inlineCode>{`, `}<inlineCode parentName="p">{`space`}</inlineCode>{`, etc), it’s typically simpler to understand a theme when values are defined in one file.
However, especially for projects with many `}<a parentName="p" {...{
        "href": "/styling-mdx"
      }}>{`styles`}</a>{` or `}<a parentName="p" {...{
        "href": "/guides/variants"
      }}>{`variants`}</a>{`, you may want to split values that end up
as part of one object across multiple files. You can use the same kind of imports combined with the `}<inlineCode parentName="p">{`merge`}</inlineCode>{` utility for this.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`// example theme/index.js
import { merge } from 'theme-ui'
import colors from './colors'
import fonts from './fonts'
import textStyles from './styles/text'
import tableStyles from './styles/tables'
import { buttons, forms, links } from './variants'

export default {
  colors,
  fonts,
  styles: merge(textStyles, tableStyles),
  buttons,
  forms,
  links,
}
`}</code></pre>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      